import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, KeyRound, ArrowLeft } from 'lucide-react'
import { resetPasswordApi } from '../api/auth.api'

const resetPasswordSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'min8')
    .regex(/[A-Z]/, 'uppercase')
    .regex(/[a-z]/, 'lowercase')
    .regex(/\d/, 'digit'),
  confirmPassword: z.string().min(1, 'required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'mismatch',
  path: ['confirmPassword'],
})

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>

export function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [error, setError] = useState<string | null>(null)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const newPassword = watch('newPassword', '')

  const passwordChecks = [
    { key: 'length', label: '8 caractères minimum', valid: newPassword.length >= 8 },
    { key: 'upper', label: '1 majuscule', valid: /[A-Z]/.test(newPassword) },
    { key: 'lower', label: '1 minuscule', valid: /[a-z]/.test(newPassword) },
    { key: 'digit', label: '1 chiffre', valid: /\d/.test(newPassword) },
  ]

  if (!token) {
    return (
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-error/20 to-error/10">
            <KeyRound className="h-8 w-8 text-error" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
              {t('auth.resetPassword.invalidLink')}
            </h1>
            <p className="text-sm text-neutral-500">
              {t('auth.resetPassword.invalidLinkMessage')}
            </p>
          </div>
        </div>
        <Link
          to="/forgot-password"
          className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-neutral-300 bg-white px-6 text-sm font-medium text-neutral-700 transition-all hover:bg-neutral-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('auth.forgotPassword.title')}
        </Link>
      </div>
    )
  }

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setError(null)
    try {
      await resetPasswordApi(token, values.newPassword)
      navigate('/login', { replace: true })
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } }
      setError(axiosError.response?.data?.message || t('auth.resetPassword.error'))
    }
  }

  return (
    <div className="space-y-8">
      {/* Icon + Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20">
          <KeyRound className="h-8 w-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
            {t('auth.resetPassword.title')}
          </h1>
          <p className="text-sm text-neutral-500 leading-relaxed max-w-sm mx-auto">
            {t('auth.resetPassword.subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div className="rounded-lg bg-error/10 border border-error/20 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* New Password */}
        <div className="space-y-1.5">
          <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700">
            {t('auth.resetPassword.newPassword')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              <Lock className="h-4 w-4" />
            </span>
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 pl-10 pr-10 text-sm transition-all placeholder:text-neutral-400 hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50"
              {...register('newPassword')}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              aria-label={showNewPassword ? t('auth.changePassword.hidePassword') : t('auth.changePassword.showPassword')}
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* Password strength indicators */}
          {newPassword.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
              {passwordChecks.map((check) => (
                <div key={check.key} className="flex items-center gap-1.5">
                  <div className={`h-1.5 w-1.5 rounded-full transition-colors ${check.valid ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
                  <span className={`text-xs transition-colors ${check.valid ? 'text-emerald-600' : 'text-neutral-400'}`}>
                    {check.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700">
            {t('auth.resetPassword.confirmPassword')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              <Lock className="h-4 w-4" />
            </span>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 pl-10 pr-10 text-sm transition-all placeholder:text-neutral-400 hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50"
              {...register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              aria-label={showConfirmPassword ? t('auth.changePassword.hidePassword') : t('auth.changePassword.showPassword')}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirmPassword?.message === 'mismatch' && (
            <p className="text-xs text-error">{t('auth.resetPassword.mismatch')}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-gradient-to-r from-primary to-primary-800 px-6 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:from-primary-800 hover:to-primary-950 hover:shadow-xl hover:shadow-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('auth.resetPassword.submitting')}
            </>
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {t('auth.resetPassword.submit')}
            </>
          )}
        </button>
      </form>
    </div>
  )
}
